/**
 * SettingsScreen - Volume control and settings
 * Displays a volume slider and a back button.
 */

export class SettingsScreen {
  constructor(game) {
    this.game = game;
    this.volume = 1.0;
    this.draggingSlider = false;
    this.backHovered = false;
    this.sliderHovered = false;
    this._loadVolume();

    // Starfield for visual consistency
    this.stars = [];
    this._initStars(80);
  }

  _initStars(count) {
    this.stars = [];
    for (let i = 0; i < count; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.01 + 0.003,
        brightness: Math.random() * 0.5 + 0.2,
        twinkleSpeed: Math.random() * 2 + 1,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
  }

  enter(data = {}) {
    this._loadVolume();
    this.draggingSlider = false;
    this.backHovered = false;
    this._updateOverlay();
  }

  exit() {
    this._saveVolume();
    if (this.game.uiOverlay) {
      this.game.uiOverlay.innerHTML = '';
    }
  }

  _loadVolume() {
    try {
      const saved = localStorage.getItem('missileCommand_volume');
      if (saved !== null) {
        this.volume = parseFloat(saved);
      }
    } catch (e) {
      // localStorage not available
    }
  }

  _saveVolume() {
    try {
      localStorage.setItem('missileCommand_volume', this.volume.toString());
    } catch (e) {
      // localStorage not available
    }
    // Update audio manager
    if (this.game.audio) {
      this.game.audio.setVolume(this.volume);
    }
    // Update DOM overlay
    this._updateOverlay();
  }

  /**
   * Get the slider track geometry for hit testing
   */
  _getSliderGeometry() {
    const w = this.game.width;
    const h = this.game.height;
    return {
      x: w * 0.25,
      w: w * 0.5,
      y: h * 0.48,
      h: 8,
      hitPadding: 25, // extra pixels above/below for touch-friendly tapping (total height 58px)
    };
  }

  /**
   * Check if position is within the slider track area
   */
  _isOverSlider(px, py) {
    const s = this._getSliderGeometry();
    return px >= s.x && px <= s.x + s.w &&
           py >= s.y - s.hitPadding && py <= s.y + s.h + s.hitPadding;
  }

  /**
   * Calculate volume from x position on slider track
   */
  _volumeFromPosition(px) {
    const s = this._getSliderGeometry();
    const ratio = (px - s.x) / s.w;
    return Math.max(0, Math.min(1, ratio));
  }

  /**
   * Check if position is within the Back button hit area
   */
  _isOverBackButton(x, y) {
    const w = this.game.width;
    const h = this.game.height;
    const btnY = h * 0.75;
    const btnWidth = Math.max(150, w * 0.2); // Min 150px for touch
    const btnHeight = Math.max(44, h * 0.06); // Min 44px for touch targets
    const left = w / 2 - btnWidth / 2;
    const top = btnY - btnHeight / 2;
    return x >= left && x <= left + btnWidth && y >= top && y <= top + btnHeight;
  }

  update(dt) {
    const input = this.game.input;

    // Animate starfield
    for (const star of this.stars) {
      star.y += star.speed * dt;
      star.twinklePhase += star.twinkleSpeed * dt;
      if (star.y > 1) {
        star.y = 0;
        star.x = Math.random();
      }
    }

    if (input.isKeyJustPressed('Escape') || input.isKeyJustPressed('Backspace')) {
      this._navigateBack();
      return;
    }

    if (input.isKeyJustPressed('ArrowLeft')) {
      this.volume = Math.max(0, Math.round((this.volume - 0.1) * 10) / 10);
      this._saveVolume();
    }
    if (input.isKeyJustPressed('ArrowRight')) {
      this.volume = Math.min(1, Math.round((this.volume + 0.1) * 10) / 10);
      this._saveVolume();
    }
    if (input.isKeyJustPressed('Enter')) {
      this._navigateBack();
    }

    // Mouse/touch interaction
    const pos = input.getPointerPosition();
    this.backHovered = this._isOverBackButton(pos.x, pos.y);
    this.sliderHovered = this._isOverSlider(pos.x, pos.y);

    // Handle slider dragging
    if (input.mouseDown && this.draggingSlider) {
      const newVolume = this._volumeFromPosition(pos.x);
      if (newVolume !== this.volume) {
        this.volume = newVolume;
        this._saveVolume();
      }
    }

    if (input.wasClicked()) {
      if (this._isOverSlider(pos.x, pos.y)) {
        this.draggingSlider = true;
        this.volume = this._volumeFromPosition(pos.x);
        this._saveVolume();
      } else if (this._isOverBackButton(pos.x, pos.y)) {
        this._navigateBack();
      }
    }

    // Stop dragging when mouse released
    if (!input.mouseDown) {
      this.draggingSlider = false;
    }
  }

  _navigateBack() {
    const prev = this.game.state.previousState;
    if (prev === 'paused') {
      this.game.state.setState('paused');
    } else {
      this.game.state.setState('title');
    }
  }

  /**
   * Update the DOM overlay with settings screen content for Playwright testing
   */
  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    const volumePercent = Math.round(this.volume * 100);

    this.game.uiOverlay.innerHTML = `
      <div data-screen="settings">
        <div class="screen-title">SETTINGS</div>
        <div class="volume-control">
          <span class="volume-label">VOLUME</span>
          <div class="volume-slider" data-volume="${volumePercent}">
            <div class="slider-track"></div>
            <div class="slider-fill" style="width: ${volumePercent}%"></div>
            <div class="slider-handle"></div>
          </div>
          <span class="volume-value">${volumePercent}%</span>
        </div>
        <div class="back-button" data-action="back">BACK</div>
      </div>
    `;
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    this.game.renderer.clearBackground();

    // Starfield background
    this._renderStarfield(ctx, w, h);

    // Title
    this.game.renderer.drawGlowText('SETTINGS', w / 2, h * 0.2, {
      font: `${Math.max(28, w * 0.035)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#00ffff',
      glowSize: 15,
    });

    // Horizontal line under title
    ctx.save();
    ctx.strokeStyle = '#00ffff';
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(w * 0.1, h * 0.27);
    ctx.lineTo(w * 0.9, h * 0.27);
    ctx.stroke();
    ctx.restore();

    // Volume label
    this.game.renderer.drawGlowText('VOLUME', w / 2, h * 0.4, {
      font: `${Math.max(18, w * 0.025)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#333333',
      glowSize: 5,
    });

    // Volume slider track
    const s = this._getSliderGeometry();

    // Track background
    ctx.save();
    ctx.fillStyle = '#222222';
    ctx.strokeStyle = '#444444';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(s.x, s.y, s.w, s.h, 4);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Track fill
    ctx.save();
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.roundRect(s.x, s.y, s.w * this.volume, s.h, 4);
    ctx.fill();
    ctx.restore();

    // Slider handle
    const handleX = s.x + s.w * this.volume;
    const handleRadius = this.sliderHovered || this.draggingSlider ? 12 : 10;
    this.game.renderer.drawGlowCircle(handleX, s.y + s.h / 2, handleRadius, {
      color: '#00ffff',
      glowColor: '#00ffff',
      glowSize: this.sliderHovered || this.draggingSlider ? 15 : 10,
    });

    // Volume percentage
    this.game.renderer.drawGlowText(`${Math.round(this.volume * 100)}%`, w / 2, h * 0.55, {
      font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
      color: '#00ff88',
      glowColor: '#00ff88',
      glowSize: 5,
    });

    // Instructions
    this.game.renderer.drawGlowText('CLICK SLIDER OR USE LEFT/RIGHT ARROWS', w / 2, h * 0.62, {
      font: `${Math.max(10, w * 0.012)}px "Share Tech Mono", monospace`,
      color: '#8899aa',
      glowColor: '#445566',
      glowSize: 2,
    });

    // Back button with hover effect
    const backColor = this.backHovered ? '#00ffff' : '#aabbcc';
    const backGlow = this.backHovered ? 15 : 5;
    this.game.renderer.drawGlowText('[ BACK ]', w / 2, h * 0.75, {
      font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
      color: backColor,
      glowColor: this.backHovered ? '#00ffff' : '#445566',
      glowSize: backGlow,
    });
  }

  _renderStarfield(ctx, w, h) {
    for (const star of this.stars) {
      const twinkle = 0.5 + 0.5 * Math.sin(star.twinklePhase);
      const alpha = star.brightness * twinkle;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
